import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import { fetchAdminDashboardStats, fetchAdminOrders } from '@/lib/api/orders';
import type { AdminDashboardStats, AdminOrderRecord } from '@/lib/domain';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  confirmed: 'bg-green-500/20 text-green-400',
  shipped: 'bg-sky-500/20 text-sky-400',
};

export default function AdminDashboardContent() {
  const { theme } = useTheme();
  const { products } = useProductCatalog();
  const dark = theme === 'dark';
  const [stats, setStats] = useState<AdminDashboardStats>({
    totalProducts: products.length,
    activeDealers: 0,
    pendingOrders: 0,
    dealerApplications: 0,
    totalClaims: 0,
    totalContactSubmissions: 0,
    newContactSubmissions: 0,
  });
  const [recentOrders, setRecentOrders] = useState<AdminOrderRecord[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [nextStats, nextOrders] = await Promise.all([
          fetchAdminDashboardStats(),
          fetchAdminOrders(),
        ]);
        setStats(nextStats);
        setRecentOrders(nextOrders.slice(0, 5));
      } catch {
        setStats((current) => ({
          ...current,
          totalProducts: products.length,
        }));
      }
    };

    void load();
  }, [products.length]);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'border-white/10' : 'border-gray-100';

  return (
    <div className="space-y-6">
      <div className={`${card} rounded-lg p-4`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex flex-wrap gap-6">
            {[
              { label: 'Catalog SKUs', value: stats.totalProducts.toString(), icon: 'ri-battery-2-charge-line', change: 'Live catalog' },
              { label: 'Active Dealers', value: stats.activeDealers.toString(), icon: 'ri-store-2-line', change: 'Portal enabled' },
              { label: 'Pending Orders', value: stats.pendingOrders.toString(), icon: 'ri-file-list-3-line', change: 'Needs action' },
              { label: 'New Inbox', value: stats.newContactSubmissions.toString(), icon: 'ri-inbox-line', change: 'Needs reply' },
              { label: 'Warranty Claims', value: stats.totalClaims.toString(), icon: 'ri-shield-check-line', change: 'Tracked in cPanel API' },
            ].map((a, i) => (
              <div key={i} className="flex items-center gap-3">
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}
                >
                  <i className={`${a.icon} text-sm`} style={{ color: 'var(--accent)' }}></i>
                </div>
                <div>
                  <p className={`text-base font-black ${text}`}>{a.value}</p>
                  <p className={`text-xs ${muted}`}>{a.label} <span className="text-green-400 font-semibold">{a.change}</span></p>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/admin/analytics"
            className="flex items-center gap-2 text-xs font-bold hover:underline cursor-pointer whitespace-nowrap shrink-0"
            style={{ color: 'var(--accent)' }}
          >
            Full Analytics <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        {[
          { icon: 'ri-battery-2-charge-line', label: 'Total Products', value: stats.totalProducts.toString(), change: 'Live product catalog', up: true },
          { icon: 'ri-store-2-line', label: 'Active Dealers', value: stats.activeDealers.toString(), change: 'Approved + portal access', up: true },
          { icon: 'ri-file-list-3-line', label: 'Pending Orders', value: stats.pendingOrders.toString(), change: 'Awaiting review', up: true },
          { icon: 'ri-user-line', label: 'Dealer Applications', value: stats.dealerApplications.toString(), change: 'Awaiting approval', up: false },
          { icon: 'ri-inbox-line', label: 'Contact Inbox', value: stats.totalContactSubmissions.toString(), change: `${stats.newContactSubmissions} new message${stats.newContactSubmissions === 1 ? '' : 's'}`, up: stats.newContactSubmissions > 0 },
        ].map((s, i) => (
          <div key={i} className={`${card} rounded-lg p-5`}>
            <div className="flex items-center justify-between mb-3">
              <div
                className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}
              >
                <i className={`${s.icon} text-lg`} style={{ color: 'var(--accent)' }}></i>
              </div>
              <span className={`text-xs ${s.up ? 'text-green-400' : 'text-yellow-400'}`}>
                {s.change}
              </span>
            </div>
            <p className={`text-2xl font-black ${text}`}>{s.value}</p>
            <p className={`text-xs mt-1 ${muted}`}>{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${card} rounded-lg p-5 lg:col-span-2`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className={`text-xs font-black uppercase tracking-[0.22em] ${muted}`}>Inbox Watch</p>
              <h2 className={`text-lg font-black mt-1 ${text}`}>Contact team queue</h2>
              <p className={`text-sm mt-1 ${muted}`}>
                {stats.newContactSubmissions > 0
                  ? `${stats.newContactSubmissions} new website message${stats.newContactSubmissions === 1 ? '' : 's'} waiting for a response.`
                  : 'No new website messages are waiting in the inbox right now.'}
              </p>
            </div>
            <Link
              to="/admin/contact-submissions"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white whitespace-nowrap"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              Open Contact Inbox
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className={`${card} rounded-lg p-5`}>
          <p className={`text-xs font-black uppercase tracking-[0.22em] ${muted}`}>Support Split</p>
          <div className="mt-4 space-y-4">
            {[
              { label: 'New', count: stats.newContactSubmissions, color: '#F59E0B' },
              { label: 'Handled', count: Math.max(stats.totalContactSubmissions - stats.newContactSubmissions, 0), color: '#10B981' },
            ].map((item) => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-xs font-medium ${text}`}>{item.label}</span>
                  <span className={`text-xs font-bold ${text}`}>{item.count}</span>
                </div>
                <div className={`w-full h-1.5 rounded-full ${dark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{
                      width: `${stats.totalContactSubmissions ? (item.count / stats.totalContactSubmissions) * 100 : 0}%`,
                      backgroundColor: item.color,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${card} rounded-lg lg:col-span-2`}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${divider}`}>
            <h2 className={`font-bold text-sm ${text}`}>Recent Orders</h2>
            <span className={`text-xs ${muted}`}>Live order feed</span>
          </div>
          <div className="divide-y divide-white/5">
            {recentOrders.length === 0 ? (
              <div className={`px-5 py-10 text-sm ${muted}`}>No orders have been created yet.</div>
            ) : (
              recentOrders.map((order) => (
                <div key={order.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className={`text-sm font-semibold ${text}`}>{order.orderNumber}</p>
                    <p className={`text-xs ${muted}`}>{order.dealer}</p>
                    <p className={`text-xs ${muted} mt-0.5`}>{order.items}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusColors[order.status]}`}>
                      {order.status}
                    </span>
                    <p className={`text-xs ${muted} mt-1`}>{order.date}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={`${card} rounded-lg`}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${divider}`}>
            <h2 className={`font-bold text-sm ${text}`}>Products by Category</h2>
          </div>
          <div className="p-5 space-y-4">
            {[
              { label: 'Lithium LiFePO4', count: products.filter((p) => p.category === 'lithium').length, color: 'var(--accent)' },
              { label: 'AGM Series', count: products.filter((p) => p.category === 'agm').length, color: '#22c55e' },
              { label: 'Gel Series', count: products.filter((p) => p.category === 'gel').length, color: '#eab308' },
            ].map((cat) => (
              <div key={cat.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-xs font-medium ${text}`}>{cat.label}</span>
                  <span className={`text-xs font-bold ${text}`}>{cat.count} SKUs</span>
                </div>
                <div className={`w-full h-1.5 rounded-full ${dark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{
                      width: `${products.length ? (cat.count / products.length) * 100 : 0}%`,
                      backgroundColor: cat.color,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 pb-5">
            <a
              href="/admin/products"
              className="block w-full text-center text-white text-xs font-bold py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              Manage Products
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
